import {
  FAVORITES_STORAGE_KEY,
  parseFavoriteIds,
  serializeFavoriteIds,
  toggleFavoriteId,
} from '@redevicer/shared'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

const FAVORITES_EVENT = 'redevicer:favorites-updated'

function readIds(): number[] {
  return parseFavoriteIds(localStorage.getItem(FAVORITES_STORAGE_KEY))
}

function persist(ids: number[]) {
  localStorage.setItem(FAVORITES_STORAGE_KEY, serializeFavoriteIds(ids))
  window.dispatchEvent(new Event(FAVORITES_EVENT))
}

type FavoritesAuthContextValue = {
  loggedIn: boolean
  ids: number[]
  has: (id: number) => boolean
  toggle: (id: number) => void
  remove: (id: number) => void
  /** Drop IDs that no longer resolve (deleted listings, stale dev data). */
  reconcileIds: (stillValidIds: readonly number[]) => void
  count: number
  onRequireLogin: () => void
}

const FavoritesAuthContext = createContext<FavoritesAuthContextValue | null>(null)

export function FavoritesAuthProvider({
  loggedIn,
  onRequireLogin,
  children,
}: {
  loggedIn: boolean
  onRequireLogin: () => void
  children: ReactNode
}) {
  const [ids, setIds] = useState<number[]>(() => readIds())

  useEffect(() => {
    function sync() {
      setIds(readIds())
    }
    window.addEventListener(FAVORITES_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(FAVORITES_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const has = useCallback(
    (id: number) => loggedIn && ids.includes(id),
    [ids, loggedIn],
  )

  const toggle = useCallback(
    (id: number) => {
      if (!loggedIn) {
        onRequireLogin()
        return
      }
      setIds((prev) => {
        const next = toggleFavoriteId(prev, id)
        persist(next)
        return next
      })
    },
    [loggedIn, onRequireLogin],
  )

  const remove = useCallback(
    (id: number) => {
      if (!loggedIn) return
      setIds((prev) => {
        const next = prev.filter((x) => x !== id)
        persist(next)
        return next
      })
    },
    [loggedIn],
  )

  const reconcileIds = useCallback(
    (stillValidIds: readonly number[]) => {
      if (!loggedIn) return
      const valid = new Set(stillValidIds)
      setIds((prev) => {
        const next = prev.filter((id) => valid.has(id))
        if (next.length === prev.length) return prev
        persist(next)
        return next
      })
    },
    [loggedIn],
  )

  const visibleIds = loggedIn ? ids : []
  const value = useMemo(
    () => ({
      loggedIn,
      ids: visibleIds,
      has,
      toggle,
      remove,
      reconcileIds,
      count: visibleIds.length,
      onRequireLogin,
    }),
    [loggedIn, visibleIds, has, toggle, remove, reconcileIds, onRequireLogin],
  )

  return <FavoritesAuthContext.Provider value={value}>{children}</FavoritesAuthContext.Provider>
}

export function useFavoritesAuth() {
  const ctx = useContext(FavoritesAuthContext)
  if (!ctx) {
    throw new Error('useFavoritesAuth must be used within FavoritesAuthProvider')
  }
  return ctx
}
